import styled from '@emotion/styled';
import {
  Button,
  ToggleButton,
  Typography,
} from '@innovamat/glimmer-components';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigation } from '../../../navigation/hooks/use-navigation';
import { useSessionDone } from '../../hooks/session-done-provider';
import { useGetApplets } from '../../hooks/use-get-applets';
import { useMarkSessionDone } from '../../hooks/use-mark-session-done';
import { useSessionFeedback } from '../../hooks/use-session-feedback';
import {
  ActivateSolutionsWrapper,
  Divider,
  Solutions,
} from './session-done-modal.styles';
import { UnlockModal } from './unlock-modal-content';
import UnlockedAppletsList from './unlocked-applets-list';

const DigitalPracticeInfoBox = styled.div`
  background-color: ${({ theme }) =>
    theme.tokens.color.alias.cm.bg['bg-neutral-subtle'].value};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const DigitalPracticeInfoBoxContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const PersonalizeButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  min-width: 207px;
`;

type Props = {
  showDigitalPracticeInformation?: boolean;
};

export function SessionDoneModal({
  showDigitalPracticeInformation = false,
}: Props): JSX.Element {
  const {
    currentSession,
    isModalOpen,
    showInModalOnlyRating,
    onCloseModal,
    t,
  } = useSessionDone();
  const { onToggleSolutions } = useMarkSessionDone();
  const queryClient = useQueryClient();
  const [isModalClosing, setIsModalClosing] = useState(false);

  const { data: applets, isError, isPending } = useGetApplets();

  const {
    hasAlreadyValue,
    isSendReviewPending,
    ratingValue,
    review,
    handleResetRating,
    onChangeRating,
    onChangeReview,
    onErrorReview,
    onSendReview,
  } = useSessionFeedback();

  const handleOnFinishedClose = (): void => {
    queryClient.invalidateQueries({ queryKey: ['getAppletsOfTheWeek'] });
    onCloseModal();
  };

  const { navigateInSameNavType } = useNavigation();

  const renderApplets = (): JSX.Element | null => {
    return (
      <>
        {showDigitalPracticeInformation ? (
          <DigitalPracticeInfoBox>
            <DigitalPracticeInfoBoxContent>
              <Typography.Body1>
                {t('session.unlockModal.appletsAdded')}
              </Typography.Body1>
              <PersonalizeButtonWrapper>
                <Button
                  variant="accent"
                  rightIcon="OpenNewTabIcon"
                  onClick={() => {
                    navigateInSameNavType(
                      '/general/page?toDigitalPracticeSlider=true',
                      undefined,
                      undefined,
                      true
                    );
                  }}
                >
                  {t('session.unlockModal.personalizePractice')}
                </Button>
              </PersonalizeButtonWrapper>
            </DigitalPracticeInfoBoxContent>
            <UnlockedAppletsList
              applets={applets}
              isError={isError}
              isLoadingApplets={isPending}
              isModalClosing={isModalClosing}
              showDigitalPracticeInformation={showDigitalPracticeInformation}
              onFinishedClose={handleOnFinishedClose}
            />
          </DigitalPracticeInfoBox>
        ) : (
          <UnlockedAppletsList
            applets={applets}
            isError={isError}
            isLoadingApplets={isPending}
            isModalClosing={isModalClosing}
            showDigitalPracticeInformation={showDigitalPracticeInformation}
            onFinishedClose={handleOnFinishedClose}
          />
        )}
      </>
    );
  };

  const renderSolutionSubtitle = (): JSX.Element => {
    if (!currentSession?.has_solutions) return <></>;
    return (
      <>
        <Solutions>
          <Typography.Body1>
            {showDigitalPracticeInformation ? (
              <Trans i18nKey="session.unlockModal.subtitle" />
            ) : (
              <Trans i18nKey="session.unlockModal.subtitle.accessToActivities" />
            )}
          </Typography.Body1>
          <ActivateSolutionsWrapper>
            <ToggleButton
              onToggle={(value) => {
                currentSession &&
                  onToggleSolutions({ resource: currentSession, value });
              }}
              isActive={currentSession?.solutions_enabled}
              dataTestId="activateSolutionsModal"
              aria-label="Activate solutions button"
            />
            <Typography.Subtitle1>
              {t('session.unlockModal.includeSolutions')}
            </Typography.Subtitle1>
          </ActivateSolutionsWrapper>
        </Solutions>
        <Divider />
      </>
    );
  };

  return (
    <UnlockModal
      t={t}
      isOpen={isModalOpen}
      onClose={onCloseModal}
      setIsModalClosing={setIsModalClosing}
      handleSendReview={onSendReview}
      solutions={renderSolutionSubtitle()}
      applets={renderApplets()}
      handleRating={onChangeRating}
      ratingValue={ratingValue}
      handleReview={onChangeReview}
      handleErrorReview={onErrorReview}
      hasAlreadyValue={hasAlreadyValue}
      isOnlyRating={showInModalOnlyRating}
      isSendReviewPending={isSendReviewPending}
      handleResetRating={handleResetRating}
      review={review}
      showDigitalPracticeInformation={showDigitalPracticeInformation}
    />
  );
}
